import { useAppDispatch, useAppSelector } from "@builder/store/store";
import "./style.scss";
import { getUIComponent, NodeEdit, noFilterIcons, RenderBuildNode } from "@builder/store/slices/BuilderSlice/builderMap";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { BuildComponentKey } from "@common/build/types";
import SidebarHeader from "@builder/components/app/SidebarHeader";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { editMap } from "@builder/components/app/EditElement/EditMap";
import OliverText from "@builder/components/general/OliverText";
import StateSelector from "@builder/components/general/StateSelector";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import useExcludeFunction from "./useExcludeFunction";
import TooltipElement from "@builder/components/general/Tooltip";

const editOptions: { value: keyof NodeEdit; icon: string }[] = [
    { value: "editor", icon: "/editor-icon.svg" },
    { value: "style", icon: "/style-icon.svg" },
    { value: "advanced", icon: "/advanced-icon.svg" }
];

const EditElement = () => {
    const node = useAppSelector(getSelectedNode) as RenderBuildNode;
    const { t } = useTranslation();
    const [currentCategory, setCurrentCategory] = useState<keyof NodeEdit>("editor");
    const ref = useRef<HTMLDivElement>(null);
    const nodeUI = node?.name ? getUIComponent(node.name as BuildComponentKey) : null;
    const nodeState = useAppSelector(state => state.builder.nodeStates[node?.id || ""]);
    const dispatch = useAppDispatch();
    const exclude = useExcludeFunction();
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);

    useEffect(() => {
        if (!nodeUI) return;

        if (!nodeUI.edit[currentCategory]?.keys?.length || exclude(nodeUI.edit.editor?.exclude) || (node.noEdit && currentCategory === "editor")) {
            if (!node.noEdit && nodeUI.edit.editor?.keys?.length) {
                if (!exclude(nodeUI.edit.editor?.exclude)) {
                    setCurrentCategory("editor");
                    return;
                }
            }
            if (nodeUI.edit.style?.keys?.length) {
                if (!exclude(nodeUI.edit.style?.exclude)) {
                    setCurrentCategory("style");
                    return;
                }
            }
            if (nodeUI.edit.advanced?.keys?.length) {
                if (!exclude(nodeUI.edit.advanced?.exclude)) {
                    setCurrentCategory("advanced");
                    return;
                }
            }
        }
    }, [nodeUI, cvMode, node?.noEdit]);

    if (!node || !nodeUI)
        return (
            <div className="no-element-selected-wrapper">
                <img src="./select-element.svg" alt="" />
                <OliverText size="s" weight="medium-heavy" text={t("editElement.Please select element")} />
                <OliverText
                    size="xs"
                    weight="light"
                    text={t("editElement.In order to select an element please click in the structure panel or on the canvas.")}
                />
            </div>
        );

    return (
        <div className="edit-element-wrapper">
            <SidebarHeader
                onRenameTitle={name =>
                    dispatch(
                        updateNodeProperty({
                            propertyPath: ["customName"],
                            value: name
                        })
                    )
                }
                title={node?.customName || t(node?.name || "")}
                icon={nodeUI.icon}
                sidebar="left"
                noTranslate={!!node?.customName}
                noFilter={noFilterIcons.includes(nodeUI.icon)}
            />
            <div className="edit-category-wrapper">
                {editOptions.map((option, index) => {
                    if (option.value === "editor" && node.noEdit) return null;
                    if ((nodeUI.edit[option.value]?.keys?.length || 0) > 0) {
                        if (exclude(nodeUI.edit[option.value]?.exclude)) return null;
                        return (
                            <div key={index}>
                                <TooltipElement
                                    text={t("editElement.tooltip." + option.value)}
                                    position="bottom"
                                    extraStyle={option.value === "editor" ? { width: "200px" } : {}}
                                >
                                    <label>
                                        <input
                                            type="radio"
                                            checked={option.value === currentCategory}
                                            onChange={() => setCurrentCategory(option.value)}
                                        />
                                        <div className="custom-cat-radio">
                                            <img src={option.icon} alt="" />
                                        </div>
                                    </label>
                                    <div className="cat-divider"></div>
                                </TooltipElement>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
            {currentCategory === "style" && nodeUI.states && nodeUI.states.length > 1 && (
                <div className="state-selector-container">
                    <StateSelector nodeId={node.id} state={nodeState} stateOptions={nodeUI.states} />
                </div>
            )}
            <div className="editables-wrapper" ref={ref}>
                {nodeUI.edit[currentCategory]?.keys?.map((editable, index) => {
                    const { key, ...editableProps } = editable;
                    const EditableComponent = editMap[key];
                    return <EditableComponent key={index} {...editableProps} parent={ref.current || undefined} stateMode={nodeState} />;
                })}
            </div>
        </div>
    );
};

export default EditElement;
